/**
 * message-contact-dialog.tsx — Nút "Nhắn tin" ở hồ sơ khách: sang màn nhắn tin của khách.
 *   - đã có hội thoại ở MỘT kênh → vào thẳng hội thoại đó;
 *   - nhiều kênh → hỏi chọn kênh để nhắn;
 *   - chưa có hội thoại nào → chọn một kênh đang kết nối để bắt đầu (POST /conversations/start, mode 'contact').
 */
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2, MessageSquare } from 'lucide-react'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { platformLabelOf } from '@/lib/platform-labels'
import { useZaloAccounts } from '@/hooks/use-integrations'
import { useStartConversation } from '@/hooks/use-conversations'
import { isUsableAccount } from '@/pages/conversations/new-conversation-lib'
import { threadsByChannel, type ContactThread } from './message-contact-lib'

export function MessageContactDialog({
  contactId, name, conversations, open, onOpenChange,
}: { contactId: string; name: string; conversations: ContactThread[]; open: boolean; onOpenChange: (v: boolean) => void }) {
  const { t } = useTranslation('custList')
  const navigate = useNavigate()
  const threads = threadsByChannel(conversations)
  const hasThreads = threads.length > 0
  const { data: accounts = [], isLoading } = useZaloAccounts(undefined, true, open && !hasThreads)
  const start = useStartConversation()
  const platformOf = new Map(accounts.map((a) => [a.id, a.platform]))
  const go = (conversationId: string) => {
    onOpenChange(false)
    navigate(`/conversations/${conversationId}`)
  }
  const startOn = (accountId: string) =>
    start.mutate({ accountId, mode: 'contact', contactId } as never, { onSuccess: (r) => go(r.conversationId), onError: (e) => toast.error(apiError(e)) })

  const usable = accounts.filter(isUsableAccount)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('messageContact.title', { name })}</DialogTitle>
          <DialogDescription>
            {hasThreads ? t('messageContact.descMulti') : t('messageContact.descNone')}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-72 space-y-1.5 overflow-y-auto">
          {hasThreads ? (
            threads.map((th) => (
              <button key={th.conversationId} type="button" onClick={() => go(th.conversationId)} className="flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left hover:bg-muted/60">
                <MessageSquare className="h-4 w-4 shrink-0 text-primary" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{th.accountName}</span>
                  <span className="block text-xs text-muted-foreground">
                    {platformOf.has(th.accountId) ? `${platformLabelOf(platformOf.get(th.accountId))} · ` : ''}
                    {th.lastAt ? t('messageContact.lastMessage', { time: dayjs(th.lastAt).format('HH:mm DD/MM/YYYY') }) : t('messageContact.noMessage')}
                  </span>
                </span>
              </button>
            ))
          ) : isLoading ? (
            <p className="flex items-center gap-2 py-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('messageContact.loadingChannels')}</p>
          ) : usable.length === 0 ? (
            <p className="py-3 text-sm text-muted-foreground">{t('messageContact.noChannels')}</p>
          ) : (
            usable.map((a) => (
              <button key={a.id} type="button" disabled={start.isPending} onClick={() => startOn(a.id)} className="flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left hover:bg-muted/60 disabled:opacity-60">
                <MessageSquare className="h-4 w-4 shrink-0 text-primary" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{a.displayName ?? a.externalUid ?? t('messageContact.channel')}</span>
                  <span className="block text-xs text-muted-foreground">{platformLabelOf(a.platform)}</span>
                </span>
                {start.isPending && start.variables?.accountId === a.id && <Loader2 className="h-4 w-4 animate-spin" />}
              </button>
            ))
          )}
        </div>
        <div className="flex justify-end"><Button variant="ghost" onClick={() => onOpenChange(false)}>{t('common:actions.close')}</Button></div>
      </DialogContent>
    </Dialog>
  )
}
